import React, { useEffect, useState } from 'react';
import { AVISO_MS, faseDaSessao, formatarRestante } from '../lib/sessao';

const FONTE = 'var(--care-font-sans)';

/**
 * Contador de sessão — canto inferior direito, em toda tela logada. Mesma
 * pílula do hub e do CRM.
 *
 *  ok        "Sessão · 7h 42min"                     discreto, botão "Renovar"
 *  aviso     "Sessão expira em 4:59 · Renovar agora" âmbar, contagem por segundo
 *  falha     "Não consegui renovar · …"              a renovação automática falhou
 *  expirada  a pílula some; o aviso de tela inteira assume (SessaoExpiradaModal)
 *
 * Recebe `sessao` por props (e não via useAuth) para o AuthContext poder
 * montá-la sem import circular. E NÃO usa `useLocation`: o AuthProvider fica
 * FORA do BrowserRouter (App.js) — no CRM a primeira versão lançou
 * "useLocation() may be used only in the context of a <Router>" na
 * renderização e a pílula simplesmente não aparecia. Sem ErrorBoundary, um
 * componente global não pode depender de contexto de rota.
 */
const SessaoPill = ({ sessao }) => {
  const [agora, setAgora] = useState(() => Date.now());
  const expiraEm = sessao?.expiraEm ?? null;
  const restante = expiraEm === null ? null : expiraEm - agora;
  const fase = faseDaSessao(restante);
  const urgente = restante !== null && restante <= AVISO_MS;

  // Tique de 1 s só quando o número muda a cada segundo; fora do aviso, 30 s
  // basta e não acorda a árvore inteira à toa.
  useEffect(() => {
    if (expiraEm === null) return undefined;
    const id = setInterval(() => setAgora(Date.now()), urgente ? 1000 : 30000);
    return () => clearInterval(id);
  }, [expiraEm, urgente]);

  if (!sessao || expiraEm === null || sessao.expirada || restante === null || fase === 'expirada') return null;

  const alerta = fase === 'aviso' || sessao.falhouRenovar;
  const rotulo = sessao.renovando
    ? 'Renovando a sessão…'
    : sessao.falhouRenovar
      ? `Não consegui renovar · expira em ${formatarRestante(restante)}`
      : fase === 'aviso'
        ? `Sessão expira em ${formatarRestante(restante)}`
        : `Sessão · ${formatarRestante(restante)}`;
  // Leitor de tela: o contador muda a cada segundo — anunciar isso por 5 min é
  // ruído. Anuncia-se só a TRANSIÇÃO (entrou em alerta / falhou / voltou ao
  // normal), numa região viva separada cujo texto só muda com a fase.
  const anuncio = sessao.falhouRenovar
    ? 'Não consegui renovar a sessão. Use Tentar de novo.'
    : fase === 'aviso'
      ? 'A sessão expira em menos de cinco minutos. Use Renovar agora.'
      : 'Sessão ativa.';

  // Papéis do DS v3: aviso = atenção (amber), falha = erro (rust), normal = superfície + ponto online (teal).
  // A cor nunca carrega sozinha: o rótulo diz o estado e o ponto é decorativo (aria-hidden).
  const cores = sessao.falhouRenovar
    ? { border: 'var(--destructive)', bg: 'var(--care-danger-bg)', fg: 'var(--care-danger-text)', ponto: 'var(--care-danger-text)' }
    : alerta
      ? { border: 'var(--care-warning-border)', bg: 'var(--care-warning-bg)', fg: 'var(--care-warning-text)', ponto: 'var(--care-warning-text)' }
      : { border: 'var(--care-border-default)', bg: 'var(--care-bg-surface-raised)', fg: 'var(--care-text-secondary)', ponto: sessao.renovando ? 'var(--care-neutral-text)' : 'var(--care-positive-text)' };

  return (
    <div
      role="status"
      aria-live="off"
      className="sessao-pilula"
      data-fase={sessao.falhouRenovar ? 'falha' : fase}
      style={{
        // Escala do DS (§5): sticky (100). Fica abaixo das listas e popovers
        // (dropdown 200), do aviso de sessão expirada (modal 400) e do toast
        // (500), que abre no mesmo canto.
        position: 'fixed', right: 'var(--care-space-6)', bottom: 'var(--care-space-6)', zIndex: 'var(--care-z-sticky)',
        display: 'flex', alignItems: 'center', gap: 'var(--care-space-4)',
        padding: 'var(--care-space-2) var(--care-space-4) var(--care-space-2) var(--care-space-5)',
        borderRadius: 'var(--radius)', fontSize: 'var(--care-text-xs)', fontWeight: 'var(--care-weight-medium)', fontFamily: FONTE,
        border: `var(--care-border-hairline) solid ${cores.border}`, background: cores.bg, color: cores.fg,
        fontVariantNumeric: 'tabular-nums lining-nums',
      }}
    >
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 'var(--care-radius-full)', background: cores.ponto, display: 'inline-block' }} />
      <span>{rotulo}</span>
      <span
        aria-live="polite"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}
      >
        {anuncio}
      </span>
      {/* .ui-botao do kit: 32px (sm), fio no alerta e fantasma no normal; o
          tom vem do estado. Hover --secondary, pressionado overlay 10%,
          disabled .45 + not-allowed — tudo pelo kit. */}
      <button
        type="button"
        className="ui-botao"
        data-tamanho="sm"
        data-variante={alerta ? 'fio' : 'fantasma'}
        data-tom={sessao.falhouRenovar ? 'negativo' : (alerta ? 'atencao' : undefined)}
        onClick={() => { sessao.renovar(); }}
        disabled={sessao.renovando}
        aria-disabled={sessao.renovando}
        title="Renovar a sessão agora (sem sair da tela)"
      >
        {sessao.falhouRenovar ? 'Tentar de novo' : alerta ? 'Renovar agora' : 'Renovar'}
      </button>
    </div>
  );
};

export default SessaoPill;
